import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { MapService } from "@services/map.service";
import "./v-map.component.scss";

export interface ICommonMapComponent {
    readonly mapInitialisedPromise: angular.IPromise<google.maps.Map>;

    getMap(): google.maps.Map;
    fitMap(latLngs: google.maps.LatLng[] | google.maps.LatLngLiteral[]);
}

@Component({
    selector: "v-map",
    template: require("./v-map.component.html")
})
export class VMapComponent implements AfterViewInit, ICommonMapComponent {

    @Input() greyscale: boolean;
    @Input() center: google.maps.LatLng;
    @Input() zoom = 14;

    @Output() mapInitialised = new EventEmitter<google.maps.Map>();

    @ViewChild("googleMapContainer", { static: false }) googleMapContainer: ElementRef;

    // ---

    private mapReadyPromise: angular.IPromise<google.maps.Map>;
    mapInitialisedPromise: angular.IPromise<google.maps.Map>;

    private mapInitialisedPromiseResolve: (value: google.maps.Map | PromiseLike<google.maps.Map>) => void;

    private map: google.maps.Map;
    private mapOffset: google.maps.Point;

    constructor(private readonly host: ElementRef, private readonly mapService: MapService) {
        this.mapInitialisedPromise = new Promise<google.maps.Map>((resolve, reject) => { this.mapInitialisedPromiseResolve = resolve; }) as any;
    }

    ngAfterViewInit() {
        this.initialiseMap();
    }

    getMap(): google.maps.Map {
        return this.map;
    }

    // Copied from veriMap - needs 
    fitMap(latLngs: google.maps.LatLng[]) {

        if (!this.map) {

            // If the map is not available then wait until it is
            this.mapInitialisedPromise
                .then(() => { this.fitMap(latLngs); })
                .catch(e => { console.error("mapReady error", e); });

            return;
        }

        if (latLngs == null || latLngs.length == 0) {
            return;
        }

        this.mapService.setMapBoundsWithOffset(this.map, latLngs, this.getMapOffset());
    }

    // ---

    private initialiseMap() {

        // This looks complicated, but it allows child components to wait on this promise before everything is setup
        // Note we stack this as the new naming is BETTER but I didnt want to change 100 files that rely on the previous naming

        this.mapReadyPromise = this.mapService.getMapAPIReadyPromise();
        this.mapReadyPromise.then(() => {

            var options: google.maps.MapOptions = {
                center: this.center || this.mapService.getDefaultMapLocation(),
                zoom: this.zoom,
                controlSize: 24,
                styles: this.getMapStyle()
            };

            this.mapService.createMap(this.googleMapContainer.nativeElement, options).then(map => {
                this.map = map;
                this.mapInitialisedPromiseResolve(map);
                this.mapInitialised.emit(map);
            });
        });
    }

    private getMapOffset() {
        let offset = new google.maps.Point(0, 0);

        // CJE removed 7.6.21 - Unsure if this is needed anymore?
        //if (!this.disableDefaultUi) {
        //    offset.y = -60;
        //}

        if (this.mapOffset) {
            offset.x = this.mapOffset.x || offset.x;
            offset.y = this.mapOffset.y || offset.y;
        }

        return offset;
    }

    private getMapStyle(): google.maps.MapTypeStyle[] {
        if (!this.greyscale) {
            return null;
        }

        return [
            { "featureType": "administrative", "elementType": "geometry", "stylers": [{ "color": "#a7a7a7" }] },
            { "featureType": "administrative", "elementType": "labels.text.fill", "stylers": [{ "visibility": "on" }, { "color": "#737373" }] },
            { "featureType": "landscape", "elementType": "geometry.fill", "stylers": [{ "visibility": "on" }, { "color": "#efefef" }] },
            { "featureType": "poi", "elementType": "geometry.fill", "stylers": [{ "visibility": "on" }, { "color": "#dadada" }] },
            { "featureType": "poi", "elementType": "labels", "stylers": [{ "visibility": "off" }] },
            { "featureType": "poi", "elementType": "labels.icon", "stylers": [{ "visibility": "off" }] },
            { "featureType": "road", "elementType": "labels.text.fill", "stylers": [{ "color": "#696969" }, { "visibility": "on" }] },
            { "featureType": "road", "elementType": "labels.icon", "stylers": [{ "visibility": "off" }] },
            { "featureType": "road.highway", "elementType": "geometry.fill", "stylers": [{ "color": "#ffffff" }, { "visibility": "on" }] },
            { "featureType": "road.highway", "elementType": "geometry.stroke", "stylers": [{ "visibility": "on" }, { "color": "#b3b3b3" }] },
            { "featureType": "road.highway", "elementType": "labels.text.fill", "stylers": [{ "invert_lightness": true }] },
            { "featureType": "road.highway", "elementType": "labels.text.stroke", "stylers": [{ "invert_lightness": true }, { "visibility": "off" }, { "saturation": -100 }] },
            { "featureType": "road.highway.controlled_access", "elementType": "labels.icon", "stylers": [{ "visibility": "on" }, { "saturation": -100 }] },
            { "featureType": "road.arterial", "elementType": "geometry.fill", "stylers": [{ "color": "#ffffff" }] },
            { "featureType": "road.arterial", "elementType": "geometry.stroke", "stylers": [{ "color": "#d6d6d6" }] },
            { "featureType": "road.arterial", "elementType": "labels.text.fill", "stylers": [{ "visibility": "on" }, { "hue": "#ff0000" }] },
            { "featureType": "road.local", "elementType": "geometry.fill", "stylers": [{ "visibility": "on" }, { "color": "#ffffff" }, { "weight": 1.8 }] },
            { "featureType": "road.local", "elementType": "geometry.stroke", "stylers": [{ "color": "#d7d7d7" }] },
            { "featureType": "transit", "elementType": "all", "stylers": [{ "color": "#808080" }, { "visibility": "off" }] },
            { "featureType": "water", "elementType": "geometry.fill", "stylers": [{ "color": "#d3d3d3" }] }
        ];
    }
}
